// Modal "Exportar relatório" (mockup Aeros): XLSX ou PDF resumido, chips de
// colunas e toggle de transcrição. A busca dos dados fica com o pai
// (onExport recebe { format, columns, transcript }).
import React, { useState } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import TableChartOutlinedIcon from "@material-ui/icons/TableChartOutlined";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";
import CheckIcon from "@material-ui/icons/Check";
import GetAppIcon from "@material-ui/icons/GetApp";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../../components/Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { EXPORT_COLUMNS, TRANSCRIPT_LIMIT, periodLabel } from "./reportRules";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const purple = stateText(theme, "#C08BFF", "purple");
  return {
    paper: { width: 480, maxWidth: "calc(100vw - 32px)", margin: 16 },
    body: { padding: "18px 24px", display: "flex", flexDirection: "column", gap: 14 },
    formats: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
    fmt: { border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`, borderRadius: 12, padding: "12px 14px", display: "flex", gap: 10, alignItems: "center", cursor: "pointer", background: "none", font: "inherit", textAlign: "left", color: theme.palette.text.primary, "& .MuiSvgIcon-root": { fontSize: 22 } },
    fmtOn: { borderColor: "rgba(160,68,255,0.4)", backgroundColor: "rgba(160,68,255,0.08)" },
    fmtTitle: { fontSize: 13, fontWeight: 600 },
    fmtSub: { fontSize: 11, color: theme.palette.text.secondary },
    label: { fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 8 },
    chips: { display: "flex", gap: 6, flexWrap: "wrap" },
    chip: { fontSize: 11.5, fontWeight: 600, borderRadius: 99, padding: "4px 11px", display: "flex", alignItems: "center", gap: 4, cursor: "pointer", font: "inherit", background: "none", border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`, color: theme.palette.text.secondary, "& .MuiSvgIcon-root": { fontSize: 12 } },
    chipOn: { color: purple, backgroundColor: alpha("#A044FF", 0.15), borderColor: "transparent" },
    row: { display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", backgroundColor: theme.palette.aeros.surface, border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`, borderRadius: 12 },
    rowTitle: { fontSize: 12.5, fontWeight: 600, color: theme.palette.text.primary },
    rowSub: { fontSize: 11, color: theme.palette.text.secondary },
    success: { color: stateText(theme, AEROS.success, "success") },
    error: { color: stateText(theme, AEROS.error, "error") },
  };
});

const t = (k, o) => i18n.t(`aeros.reports.${k}`, o);

export default function ExportReportModal({ open, onClose, onExport, total, dateFrom, dateTo, loading }) {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();
  const [fmt, setFmt] = useState("xlsx");
  const [cols, setCols] = useState(() => EXPORT_COLUMNS.filter((c) => c.on).map((c) => c.key));
  const [transcript, setTranscript] = useState(false);
  const toggle = (k) => setCols((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const tooMany = transcript && total > TRANSCRIPT_LIMIT;

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={m.form}>
        <div className={m.header}>
          <div style={{ flex: 1 }}>
            <div className={m.title}>{t("export.title")}</div>
            <div className={m.subtitle}>{total} {t("tickets")} · {periodLabel(dateFrom, dateTo)}</div>
          </div>
          <IconButton className={m.closeButton} onClick={onClose} aria-label={i18n.t("aeros.header.close")}><CloseIcon /></IconButton>
        </div>
        <div className={classes.body}>
          <div className={classes.formats}>
            <button type="button" className={`${classes.fmt} ${fmt === "xlsx" ? classes.fmtOn : ""}`} onClick={() => setFmt("xlsx")}>
              <TableChartOutlinedIcon className={classes.success} />
              <div><div className={classes.fmtTitle}>{t("export.xlsx")}</div><div className={classes.fmtSub}>{t("export.xlsxDesc")}</div></div>
            </button>
            <button type="button" className={`${classes.fmt} ${fmt === "pdf" ? classes.fmtOn : ""}`} onClick={() => setFmt("pdf")}>
              <PictureAsPdfOutlinedIcon className={classes.error} />
              <div><div className={classes.fmtTitle}>{t("export.pdf")}</div><div className={classes.fmtSub}>{t("export.pdfDesc")}</div></div>
            </button>
          </div>
          {fmt === "xlsx" && (
            <div>
              <div className={classes.label}>{t("export.columns")}</div>
              <div className={classes.chips}>
                {EXPORT_COLUMNS.map((c) => {
                  const on = cols.includes(c.key);
                  return (
                    <button key={c.key} type="button" className={`${classes.chip} ${on ? classes.chipOn : ""}`} onClick={() => toggle(c.key)} aria-pressed={on}>
                      {on && <CheckIcon />}{c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {fmt === "xlsx" && (
            <div className={classes.row}>
              <div style={{ flex: 1 }}>
                <div className={classes.rowTitle}>{t("export.transcript")}</div>
                <div className={classes.rowSub}>{tooMany ? t("export.transcriptLimit", { limit: TRANSCRIPT_LIMIT }) : t("export.transcriptDesc")}</div>
              </div>
              <Switch className={aeros.toggleSwitchSmall} checked={transcript} onChange={(e) => setTranscript(e.target.checked)} inputProps={{ "aria-label": t("export.transcript") }} />
            </div>
          )}
        </div>
        <div className={m.footer}>
          <div className={m.footerActions}>
            <Button className={m.cancelButton} onClick={onClose}>{t("cancel")}</Button>
            <Button className={m.submitButton} disabled={loading || tooMany || (fmt === "xlsx" && !cols.length)} onClick={() => onExport({ format: fmt, columns: EXPORT_COLUMNS.filter((c) => cols.includes(c.key)), transcript: fmt === "xlsx" && transcript })} startIcon={loading ? <CircularProgress size={14} /> : <GetAppIcon />}>
              {t("export.button", { count: total })}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
